import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, FadeIn } from "../components/Reveal";
import { MetaLabel } from "../components/Bits";
import { IMAGES } from "../lib/brand";
import { setSeo } from "../lib/seo";

export default function About() {
  useEffect(() => { setSeo({ title: "About Marella Bolognesi · Miami, Florida", description: "Marella Bolognesi is a working Estate Manager in Miami with a background in international hospitality, events and design. Learn about her practice and the path into Estate Management." }); }, []);

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Header */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pt-20 lg:pt-28 pb-16 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <FadeIn><MetaLabel>Who I Am · Miami, Florida</MetaLabel></FadeIn>
          <FadeIn delay={0.08}>
            <h1 className="mt-6 font-serif font-light text-ink text-5xl sm:text-6xl lg:text-[6.5vw] leading-[0.9] tracking-tight">
              Marella<br /><span className="italic">Bolognesi</span>
            </h1>
          </FadeIn>
        </div>
        <FadeIn delay={0.16} className="lg:col-span-5">
          <p className="text-lg font-light text-ink-soft leading-relaxed">
            My career has always centered around service, organization and the details people rarely see. From international events across Europe to design and, eventually, private Estate Management in Miami, each chapter taught me something about what it takes to make complex environments feel effortless.
          </p>
        </FadeIn>
      </section>

      {/* Portrait */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12">
        <FadeIn delay={0.1}>
          <div className="overflow-hidden max-w-md">
            <img src={IMAGES.portrait} alt="Marella Bolognesi" className="w-full aspect-[4/5] object-cover object-top" />
          </div>
        </FadeIn>
      </section>

      {/* Story */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <Reveal className="lg:col-span-4"><p className="text-[11px] uppercase tracking-ultra text-accent">The Practice</p></Reveal>
          <div className="lg:col-span-8 space-y-8 text-lg font-light text-ink-soft leading-relaxed max-w-2xl">
            <Reveal><p className="text-2xl lg:text-3xl font-serif text-ink leading-snug">A well-managed home should feel effortless to the people who live in it. The work behind that feeling is anything but.</p></Reveal>
            <Reveal delay={0.05}><p>Estate Management requires anticipating needs, managing people and vendors, maintaining systems, protecting the principal's privacy, solving problems before they become disruptions, and understanding that no two households operate the same way.</p></Reveal>
            <Reveal delay={0.1}><p>My approach is structured but never rigid. Systems matter, but so do observation, judgment and knowing when a home requires something different from what is written on a checklist.</p></Reveal>
            <Reveal delay={0.15}><p>That balance — between structure and service — is what has shaped the way I manage homes and, eventually, the way I began teaching the profession.</p></Reveal>
          </div>
        </div>
      </section>

      {/* The Path */}
      <section className="border-t border-line">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
            <Reveal className="lg:col-span-4"><p className="text-[11px] uppercase tracking-ultra text-accent">The Path</p></Reveal>
            <div className="lg:col-span-8 space-y-8 text-lg font-light text-ink-soft leading-relaxed max-w-2xl">
              <Reveal><p className="text-2xl lg:text-3xl font-serif text-ink leading-snug">I didn't begin my career in Estate Management.</p></Reveal>
              <Reveal delay={0.05}><p>Before private service, I spent years managing international medical congresses and events across Europe — work that taught me logistics, anticipation, discretion and how to remain calm when hundreds of details are moving at once.</p></Reveal>
              <Reveal delay={0.1}><p>Later, my interest in interiors led me to study design. When I eventually entered Estate Management in Miami, I realized I had found a profession where those seemingly separate experiences finally made sense together.</p></Reveal>
              <Reveal delay={0.15}><p className="font-serif italic text-ink text-xl lg:text-2xl">Organization. Hospitality. Homes. People. Details.</p></Reveal>
              <Reveal delay={0.2}><p>Today, those experiences shape both how I manage residences and how I teach others entering the profession.</p></Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-40 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="overflow-hidden">
              <img src={IMAGES.mission} alt="Mission" className="w-full aspect-[4/5] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[11px] uppercase tracking-ultra text-accent mb-8">My Mission</p>
            <h2 className="font-serif font-light text-4xl lg:text-6xl text-ink leading-[1.02] tracking-tight">
              To raise the standard of Estate Management — inside the home and within the profession.
            </h2>
            <p className="mt-8 text-lg font-light text-ink-soft leading-relaxed max-w-lg">
              For the homes I manage, that means thoughtful systems, discretion and service that adapts to the people who live there.
            </p>
            <p className="mt-6 text-lg font-light text-ink-soft leading-relaxed max-w-lg">
              For the professionals I teach, it means making a traditionally private profession easier to understand — and sharing the practical knowledge I wish had been easier to find when I began.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/salotto-digitale" data-testid="about-salotto-cta" className="bg-ink text-bg px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors flex items-center gap-3 group">
                Explore Il Salotto Digitale <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a href="https://www.instagram.com/boutiqueestatemanagement/" target="_blank" rel="noreferrer" data-testid="about-instagram" className="border border-ink text-ink px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bg transition-colors flex items-center gap-2">
                Follow on Instagram <ArrowUpRight size={15} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
